import { useState } from 'react';
import { ButtonDropdown } from '@cloudscape-design/components';
import type { EvaluationAuditTrail } from '@amzn/helianthus-arbiter-client';
import { auditTrailToJson, auditTrailToCsv, downloadAsFile } from '../utils/auditTrailExport';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface AuditTrailExportButtonProps {
  auditTrail: EvaluationAuditTrail;
  evaluationId: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AuditTrailExportButton({ auditTrail, evaluationId }: AuditTrailExportButtonProps): React.JSX.Element {
  const [clipboardLabel, setClipboardLabel] = useState('Copy to Clipboard');

  const handleExport = (format: string): void => {
    const safeId = evaluationId.replace(/[^a-zA-Z0-9-_]/g, '_');
    if (format === 'json') {
      const content = auditTrailToJson(auditTrail);
      downloadAsFile(content, `audit-trail-${safeId}.json`, 'application/json');
    } else if (format === 'csv') {
      const content = auditTrailToCsv(auditTrail);
      downloadAsFile(content, `audit-trail-${safeId}.csv`, 'text/csv');
    } else if (format === 'clipboard') {
      const content = auditTrailToJson(auditTrail);
      navigator.clipboard.writeText(content).then(() => {
        setClipboardLabel('Copied!');
        setTimeout(() => setClipboardLabel('Copy to Clipboard'), 2000);
      }).catch(() => {
        setClipboardLabel('Copy failed');
        setTimeout(() => setClipboardLabel('Copy to Clipboard'), 2000);
      });
    }
  };

  return (
    <ButtonDropdown
      items={[
        { id: 'json', text: 'Export as JSON' },
        { id: 'csv', text: 'Export as CSV' },
        { id: 'clipboard', text: clipboardLabel },
      ]}
      onItemClick={({ detail }) => handleExport(detail.id)}
    >
      Export
    </ButtonDropdown>
  );
}
